import { Component, OnDestroy, Inject, PLATFORM_ID, ChangeDetectorRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import * as fromRoot from '../../ng-store/index';
import * as contactActions from '../../ng-store/action/contact.action';
import { RecaptchaModule, RecaptchaFormsModule, RecaptchaComponent } from 'ng-recaptcha-19';
// Removed Angular Material - using console.log instead

export interface IResponse {
  statusCode: number,
  body: string
}

@Component({
    selector: 'app-section-contact',
    templateUrl: './section-contact.component.html',
    styleUrls: ['./section-contact.component.scss'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RecaptchaModule, RecaptchaFormsModule]
})
export class SectionContactComponent implements OnDestroy {
  @ViewChild('recaptcha') recaptcha!: RecaptchaComponent;
  contact: FormGroup;
  destroy$: Subject<boolean> = new Subject<boolean>();
  isLoading = false;
  isBrowser: boolean;
  captchaReady = false;
  successMessage = '';
  errorMessage = '';

  constructor(
    private readonly store: Store,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Create form with base controls only
    this.contact = new FormGroup({
      name: new FormControl('', Validators.required),
      email: new FormControl('', Validators.required),
      message: new FormControl('', Validators.required)
    });

    // Add captcha control after component initialization on browser side
    if (this.isBrowser) {
      setTimeout(() => {
        this.contact.addControl('captcha', new FormControl('', Validators.required));
        this.captchaReady = true;
        this.cdr.detectChanges();
      }, 0);
    }

    this
      .store
      .select(fromRoot.contact)
      .pipe(takeUntil(this.destroy$))
      .subscribe(contactState => {
        if (contactState) {
          this.isLoading = contactState.isUploading;
        }
        if (contactState && contactState.isUploading) {
          this.contact.disable();
        } else {
          this.contact.enable();
        }
        if (contactState && contactState.isUploadingSuccess || contactState && contactState.isUploadingFailure) {
          if (contactState.isUploadingSuccess) {
            this.successMessage = 'Email sent successfully! We\'ll get back to you soon.';
            this.errorMessage = '';
            this.contact.reset();
            // Reset the captcha component
            if (this.recaptcha) {
              this.recaptcha.reset();
            }
          } else if (contactState.isUploadingFailure) {
            this.errorMessage = contactState.response?.result || 'Failed to send email. Please try again.';
            this.successMessage = '';
            // Reset the captcha component on error too
            if (this.recaptcha) {
              this.recaptcha.reset();
            }
          }
          this.store.dispatch(contactActions.contactReset());
        }
      });
  }

  get name(): AbstractControl {
    return this.contact.get('name')!;
  }

  get email(): AbstractControl {
    return this.contact.get('email')!;
  }

  get message(): AbstractControl {
    return this.contact.get('message')!;
  }

  get captcha(): AbstractControl {
    return this.contact.get('captcha')!;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onSubmit(): void {
    this
      .store
      .dispatch(contactActions.contact({
        name: this.contact.get('name')?.value!,
        email: this.contact.get('email')?.value!,
        message: this.contact.get('message')?.value!,
        captcha: this.contact.get('captcha')?.value!
      }));
  }
}
